import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'

import * as api from '../api/perangkat.api.js'
import * as tukApi from '../api/tuk.api.js'
import { ApiError } from '../api/client.js'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { useAuth } from '../auth/AuthProvider.tsx'

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

const STATUS_CLASS: Partial<Record<api.DeviceStatus, string>> = {
  TERPASANG: 'badge-diterbitkan',
  RUSAK: 'badge-ditolak',
  DINONAKTIFKAN: 'badge-ditolak',
  DIKIRIM: 'badge-diajukan',
}

function StatusBadge({ status }: { status: api.DeviceStatus }): ReactNode {
  return <span className={`badge ${STATUS_CLASS[status] ?? ''}`}>{status}</span>
}

export function PerangkatPage(): ReactNode {
  const { user } = useAuth()
  const nasional = user?.roles.some((r) => r.scope === 'NASIONAL') ?? false
  const [version, setVersion] = useState(0)
  const [dibuka, setDibuka] = useState<api.Device | null>(null)
  const [status, setStatus] = useState('')
  const [ringkasan, setRingkasan] = useState<readonly api.SummaryRow[]>([])

  useEffect(() => {
    api
      .summary()
      .then(setRingkasan)
      .catch(() => setRingkasan([]))
  }, [version])

  const fetchPage = useCallback(
    (page: number, q: string) => api.listDevices(page, q, { status }),
    [status],
  )

  const COLUMNS: readonly Column<api.Device>[] = [
    { key: 'sn', label: 'Nomor Seri', render: (d) => <code>{d.serialNumber}</code> },
    {
      key: 'jenis',
      label: 'Jenis',
      render: (d) => (
        <>
          {d.type.replace(/_/g, ' ').toLowerCase()}
          {d.model === null ? '' : <span className="hint"> {d.model}</span>}
        </>
      ),
    },
    {
      key: 'tuk',
      label: 'Lokasi',
      render: (d) => d.tuk?.code ?? <span className="hint">pusat</span>,
    },
    {
      key: 'kunci',
      label: 'Kunci',
      render: (d) =>
        d.signingKeyFingerprint === null ? (
          <span className="hint">belum</span>
        ) : (
          <code title={d.signingKeyFingerprint}>{d.signingKeyFingerprint.slice(0, 10)}…</code>
        ),
    },
    { key: 'status', label: 'Status', render: (d) => <StatusBadge status={d.status} /> },
    { key: 'aksi', label: '', render: (d) => <button onClick={() => setDibuka(d)}>Buka</button> },
  ]

  if (dibuka !== null) {
    return (
      <PerangkatDetail
        awal={dibuka}
        onBack={() => {
          setDibuka(null)
          setVersion((v) => v + 1)
        }}
      />
    )
  }

  const total = ringkasan.reduce((n, r) => n + r.count, 0)
  const perStatus = new Map<string, number>()
  for (const r of ringkasan) perStatus.set(r.status, (perStatus.get(r.status) ?? 0) + r.count)

  return (
    <div>
      <h1>Perangkat</h1>
      <p className="hint">
        Pengadaan terpusat: perangkat dikirim panitia pusat, berpindah antar gelombang, rusak, masuk
        garansi, lalu pensiun. Setiap perpindahan tercatat beserta alasannya.
      </p>

      <div className="ringkas-kartu">
        <div>
          <strong>{total}</strong>
          <span>total</span>
        </div>
        {[...perStatus.entries()].map(([s, n]) => (
          <div key={s}>
            <strong>{n}</strong>
            <span>{s.toLowerCase()}</span>
          </div>
        ))}
      </div>

      <div className="page-actions">
        {nasional && <DaftarkanPerangkat onDone={() => setVersion((v) => v + 1)} />}
        <label className="inline-label">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">semua</option>
            {api.DEVICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.toLowerCase()}
              </option>
            ))}
          </select>
        </label>
      </div>

      <DataTable columns={COLUMNS} fetchPage={fetchPage} version={version} />
    </div>
  )
}

function DaftarkanPerangkat({ onDone }: { onDone: () => void }): ReactNode {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      await api.register({
        serialNumber: String(form.get('serialNumber') ?? ''),
        type: String(form.get('type') ?? 'TABLET_PENGUJI') as api.DeviceType,
        ...(form.get('model') ? { model: String(form.get('model')) } : {}),
      })
      setOpen(false)
      onDone()
    } catch (err) {
      setError(pesan(err))
    }
  }

  if (!open) return <button onClick={() => setOpen(true)}>+ Daftarkan Perangkat</button>

  return (
    <form className="inline-form" onSubmit={onSubmit}>
      <input name="serialNumber" placeholder="Nomor seri" required />
      <select name="type" defaultValue="TABLET_PENGUJI">
        {api.DEVICE_TYPES.map((t) => (
          <option key={t} value={t}>
            {t.replace(/_/g, ' ').toLowerCase()}
          </option>
        ))}
      </select>
      <input name="model" placeholder="Model" />
      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="inline-form-actions">
        <button type="submit">Simpan</button>
        <button type="button" onClick={() => setOpen(false)}>
          Batal
        </button>
      </div>
    </form>
  )
}

/**
 * Detail satu unit.
 *
 * Transisi yang ditawarkan datang dari `allowedNext` milik server, bukan dari
 * daftar yang disalin ke klien. Aturan siklus hidup hanya boleh punya satu
 * sumber; menyalinnya ke sini berarti tombol yang tersedia dan yang benar-benar
 * diterima server akan menyimpang.
 */
function PerangkatDetail({ awal, onBack }: { awal: api.Device; onBack: () => void }): ReactNode {
  const [device, setDevice] = useState<api.DeviceDetail>(awal)
  const [riwayat, setRiwayat] = useState<readonly api.Movement[]>([])
  const [tuks, setTuks] = useState<readonly tukApi.Tuk[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const muat = useCallback(async () => {
    try {
      const [d, h] = await Promise.all([api.detail(awal.id), api.history(awal.id)])
      setDevice(d)
      setRiwayat(h)
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [awal.id])

  useEffect(() => {
    void muat()
    tukApi
      .listTuk(1, '')
      .then((r) => setTuks(r.rows))
      .catch(() => setTuks([]))
  }, [muat])

  async function ubah(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    try {
      await api.changeStatus(awal.id, {
        status: String(form.get('status') ?? '') as api.DeviceStatus,
        reason: String(form.get('reason') ?? ''),
        ...(form.get('tukId') ? { tukId: String(form.get('tukId')) } : {}),
      })
      await muat()
    } catch (err) {
      setError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  // `undefined` berarti server belum menjawab, bukan "tidak ada transisi".
  // Menyamakan keduanya membuat perangkat yang baru disiapkan tampil sebagai
  // status terminal — dan operator percaya begitu saja.
  const berikutnya = device.allowedNext
  const belumDiketahui = berikutnya === undefined

  return (
    <div>
      <button onClick={onBack}>← Kembali</button>
      <h1>
        <code>{device.serialNumber}</code>
      </h1>
      <p className="hint">
        {device.type.replace(/_/g, ' ').toLowerCase()}
        {device.model === null ? '' : ` · ${device.model}`} ·{' '}
        {device.tuk === null ? 'di pusat' : `${device.tuk.code} — ${device.tuk.name}`}
      </p>

      <div className="page-actions">
        <StatusBadge status={device.status} />
        {device.signingKeyFingerprint === null ? (
          <span className="hint">kunci penandatanganan belum didaftarkan</span>
        ) : (
          <span className="hint">kunci terdaftar {device.enrolledAt?.slice(0, 10)}</span>
        )}
      </div>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {belumDiketahui ? (
        <p className="hint">Memuat transisi yang tersedia…</p>
      ) : berikutnya.length === 0 ? (
        <p className="notice">Status ini terminal; tidak ada perubahan lanjutan.</p>
      ) : (
        <form className="inline-form" onSubmit={ubah}>
          <select name="status" required defaultValue="">
            <option value="" disabled>
              Ubah status ke…
            </option>
            {berikutnya.map((s) => (
              <option key={s} value={s}>
                {s.toLowerCase()}
              </option>
            ))}
          </select>
          <select name="tukId" defaultValue="">
            <option value="">TUK tujuan (bila berpindah)</option>
            {tuks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.name}
              </option>
            ))}
          </select>
          <input name="reason" placeholder="Alasan — wajib" required />
          <div className="inline-form-actions">
            <button type="submit" disabled={busy}>
              Terapkan
            </button>
          </div>
        </form>
      )}

      <h2>Riwayat perpindahan</h2>
      {riwayat.length === 0 ? (
        <p className="dt-empty">Belum pernah berpindah.</p>
      ) : (
        <ul className="ringkas-list">
          {riwayat.map((m) => (
            <li key={m.id}>
              <span>
                {m.movedAt.slice(0, 10)} · {m.reason}
              </span>
              <span className="hint">{m.movedBy}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
